import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { CrawlScope, WebSourceConfig } from '../../types';
import { validateCrawlUrl } from '../../services/webSources';

interface AddWebsiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (config: WebSourceConfig) => Promise<void>;
}

const SCOPE_CHOICES: Array<{ value: CrawlScope; label: string; hint: string }> = [
  {
    value: 'page',
    label: 'Just this page',
    hint: 'Reads the single address you entered and nothing else.',
  },
  {
    value: 'section',
    label: 'This section and everything under it',
    hint: 'Follows links below this address. The usual choice for a docs or help centre.',
  },
  {
    value: 'host',
    label: 'The entire site',
    hint: 'Starts at the top of the domain and follows every internal link.',
  },
];

// The connector's own default for maxLinksPerUrl. Exported because the empty
// state's inline URL field adds a website without opening this dialog, and the
// two must agree on what "the defaults" means.
export const DEFAULT_MAX_LINKS_PER_URL = 100;

// Bedrock's ceiling for implicitWaitInSeconds. Measured against the live
// connector — the doc page describes the field but states no range.
const MAX_RENDER_WAIT_SECONDS = 10;

// Bedrock refuses to ingest any single file above this, so allowing a larger value
// would only mean fetching files in full and discarding them. Mirrors
// BEDROCK_MAX_FILE_SIZE_MB in stacks/kb/src/kb-web-source/handler.py, which enforces
// it — this copy is for the error message, not the guarantee.
const MAX_FILE_SIZE_MB = 50;

/** Comma or newline separated, trimmed, empties dropped. */
function splitPatterns(raw: string): string[] | undefined {
  const parts = raw
    .split(/[\n,]/)
    .map(p => p.trim())
    .filter(Boolean);
  return parts.length ? parts : undefined;
}

export default function AddWebsiteModal({ isOpen, onClose, onAdd }: AddWebsiteModalProps) {
  const [url, setUrl] = useState('');
  const [scope, setScope] = useState<CrawlScope>('section');
  const [includeSubdomains, setIncludeSubdomains] = useState(false);
  const [maxLinksPerUrl, setMaxLinksPerUrl] = useState(String(DEFAULT_MAX_LINKS_PER_URL));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [crawlRate, setCrawlRate] = useState('');
  const [excludePatterns, setExcludePatterns] = useState('');
  const [includePatterns, setIncludePatterns] = useState('');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [maxFileSizeMb, setMaxFileSizeMb] = useState('');
  const [renderWait, setRenderWait] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setUrl('');
    setScope('section');
    setIncludeSubdomains(false);
    setMaxLinksPerUrl(String(DEFAULT_MAX_LINKS_PER_URL));
    setShowAdvanced(false);
    setCrawlRate('');
    setExcludePatterns('');
    setIncludePatterns('');
    setSitemapUrl('');
    setMaxFileSizeMb('');
    setRenderWait('');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const urlError = validateCrawlUrl(url.trim());
    if (urlError) {
      setError(urlError);
      return;
    }

    if (sitemapUrl.trim()) {
      const sitemapError = validateCrawlUrl(sitemapUrl.trim());
      if (sitemapError) {
        setError(`Sitemap: ${sitemapError}`);
        return;
      }
    }

    const links = parseInt(maxLinksPerUrl, 10);
    if (!Number.isFinite(links) || links < 1 || links > 1000) {
      setError('Links per page must be between 1 and 1000');
      return;
    }

    const rate = crawlRate.trim() ? parseInt(crawlRate, 10) : undefined;
    if (rate !== undefined && (!Number.isFinite(rate) || rate < 1 || rate > 300)) {
      setError('Crawl speed must be between 1 and 300 pages per minute');
      return;
    }

    const fileSize = maxFileSizeMb.trim() ? parseInt(maxFileSizeMb, 10) : undefined;
    if (fileSize !== undefined && (!Number.isFinite(fileSize) || fileSize < 1 || fileSize > MAX_FILE_SIZE_MB)) {
      setError(`Maximum file size must be between 1 and ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    // 0 is legal and distinct from "unset", so this cannot use a truthiness test.
    const wait = renderWait.trim() ? parseInt(renderWait, 10) : undefined;
    if (wait !== undefined && (!Number.isFinite(wait) || wait < 0 || wait > MAX_RENDER_WAIT_SECONDS)) {
      setError(`Extra wait must be between 0 and ${MAX_RENDER_WAIT_SECONDS} seconds`);
      return;
    }

    // 'host' plus the subdomains checkbox is the only way to reach SUBDOMAINS.
    const effectiveScope: CrawlScope =
      scope === 'host' && includeSubdomains ? 'subdomains' : scope;

    setSubmitting(true);
    try {
      await onAdd({
        url: url.trim(),
        scope: effectiveScope,
        maxLinksPerUrl: links,
        crawlRatePerMinute: rate,
        includePatterns: splitPatterns(includePatterns),
        excludePatterns: splitPatterns(excludePatterns),
        sitemapUrl: sitemapUrl.trim() || undefined,
        maxFileSizeMb: fileSize,
        renderWaitSeconds: wait,
      });
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not add this website');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent';

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-1">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                    Add website
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                  Your chatbot will read these pages and answer from them. Pages must be public — anything behind a
                  sign-in can't be read.
                </p>

                <form onSubmit={handleSubmit}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                    Address
                  </label>
                  <input
                    type="text"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    placeholder="https://docs.example.com/"
                    className={inputClass}
                    autoFocus
                  />

                  <div className="mt-5">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      How much should we read?
                    </span>
                    <div className="space-y-2">
                      {SCOPE_CHOICES.map(choice => (
                        <label
                          key={choice.value}
                          className={clsx(
                            'flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                            scope === choice.value
                              ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-slate-700/50'
                              : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                          )}
                        >
                          <input
                            type="radio"
                            name="crawl-scope"
                            checked={scope === choice.value}
                            onChange={() => setScope(choice.value)}
                            className="mt-0.5 h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                              {choice.label}
                              {choice.value === 'section' && (
                                <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">
                                  Recommended
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {choice.hint}
                            </span>
                            {choice.value === 'host' && scope === 'host' && (
                              <label className="flex items-center gap-2 mt-2.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={includeSubdomains}
                                  onChange={e => setIncludeSubdomains(e.target.checked)}
                                  className="h-4 w-4 rounded text-gray-900 focus:ring-gray-900 border-gray-300"
                                />
                                <span className="text-xs text-gray-600 dark:text-gray-300">
                                  Also include subdomains, like blog. and help.
                                </span>
                              </label>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                      Links followed per page
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={maxLinksPerUrl}
                      onChange={e => setMaxLinksPerUrl(e.target.value)}
                      className={clsx(inputClass, 'max-w-[140px]')}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                      Following more links means a longer first read and more credits used.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAdvanced(v => !v)}
                    className="flex items-center gap-1 mt-5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    <ChevronRightIcon className={clsx('h-4 w-4 transition-transform', showAdvanced && 'rotate-90')} />
                    Advanced
                  </button>

                  {showAdvanced && (
                    <div className="mt-3 space-y-4 pl-5 border-l border-gray-200 dark:border-slate-600">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                          Skip addresses containing
                        </label>
                        <input
                          type="text"
                          value={excludePatterns}
                          onChange={e => setExcludePatterns(e.target.value)}
                          placeholder="/cart, /login, *.zip"
                          className={inputClass}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Separate with commas. Use * as a wildcard. Skipping wins over including.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                          Only read addresses containing
                        </label>
                        <input
                          type="text"
                          value={includePatterns}
                          onChange={e => setIncludePatterns(e.target.value)}
                          placeholder="/docs/, /help/"
                          className={inputClass}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Leave empty to read everything in scope.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                          Use a sitemap instead
                        </label>
                        <input
                          type="text"
                          value={sitemapUrl}
                          onChange={e => setSitemapUrl(e.target.value)}
                          placeholder="https://example.com/sitemap.xml"
                          className={inputClass}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          When set, the sitemap decides which pages are read rather than following links.
                        </p>
                      </div>

                      <div className="flex gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                            Crawl speed
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={300}
                            value={crawlRate}
                            onChange={e => setCrawlRate(e.target.value)}
                            placeholder="Default"
                            className={clsx(inputClass, 'max-w-[140px]')}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pages per minute</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                            Max file size
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={MAX_FILE_SIZE_MB}
                            value={maxFileSizeMb}
                            onChange={e => setMaxFileSizeMb(e.target.value)}
                            placeholder="10"
                            className={clsx(inputClass, 'max-w-[140px]')}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">MB</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                            Extra wait
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={MAX_RENDER_WAIT_SECONDS}
                            value={renderWait}
                            onChange={e => setRenderWait(e.target.value)}
                            placeholder="None"
                            className={clsx(inputClass, 'max-w-[140px]')}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Seconds per page
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Pages that build themselves with JavaScript are already read
                        correctly. Add a wait only if a site loads its content late and
                        comes back looking emptier than it should — it slows every page.
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="mt-4 px-3 py-2 rounded-lg text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={submitting}
                      className="px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-5 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Adding…' : 'Add website'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
